/*
* Export Association Orders for authenticated users
*/

import '@progress/kendo-ui';
import * as React from 'react';
import * as _ from 'lodash';
import moment from 'moment';
import VirtualizedSelect from 'react-virtualized-select';
import { Grid } from '@progress/kendo-grid-react-wrapper';
import DatePicker from 'react-datepicker';
import { Row, Button, ButtonToolbar } from 'react-bootstrap';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import { connectRequest, updateEntities } from 'redux-query';
import { withRouter } from 'react-router-dom';
import * as JSZip from 'jszip';
import fileDownload from 'js-file-download';

import { BootstrapColXl as Col } from '../common/bootstrap/bootstrapcolxl';
import { Formatters } from '../common/formatters';
import { FormUtilities } from '../common/formutilities';
import { GridUtilities } from '../common/grid/gridutilities';
import { NavigationLinks } from '../common/navigation';
import { OrderStatuses } from '../order/orderpricing';
import { WhiteBoxPageSection } from '../common/whiteboxpagesection';
import * as Actions from '../actions';
import * as AppComp from '../common/app-component-base';
import * as AppUtilities from '../common/apputilities';
import * as MutateActions from '../actions/mutate';
import * as ReduxQueries from '../queries';
import ApiEndpoints from '../config/apiendpoints';
import AppConfiguration from '../config/configuration';

import './dashboard.css';
import { initialState } from 'src/reducers/searchFiltersAssociationExportOrdersReducer';

(window as any).JSZip = JSZip;

export interface IAssociationExportOrdersProps extends AppComp.IAppComponentProps {
    filterByRegions: any;
    filterByStatuses: any;
    navigateTo?: any;
    associationExportOrdersFilterStatusId?: any;
    associationExportOrdersFilterRegionId?: any;
    associationExportOrdersFilterFromBidDate?: any;
    associationExportOrdersFilterToBidDate?: any;
    downloadKML?: any;
}

class AssociationExportOrders extends AppComp.AppComponent<IAssociationExportOrdersProps, any> {
    public MomentDateFormat: string = AppConfiguration.getMomentShortDateFormat();
    public MomentNow: any = moment();
    public isGridExporting: boolean = false;

    public gridOptions = GridUtilities.kendoGridOptionsPaging(
        [
            GridUtilities.kendoGridColumnDefinition('associationId', this.appTextGridColumnHeader('association-id'), 50, 300),
            GridUtilities.kendoGridColumnDefinition('associationName', this.appTextGridColumnHeader('association-name'), 150, 320),
            GridUtilities.kendoGridColumnDefinition('associationTypeName', this.appTextGridColumnHeader('association-type-name'), 100, 340),
            GridUtilities.kendoGridColumnDefinition('associationAddress', this.appTextGridColumnHeader('association-address'), 200, 360),
            GridUtilities.kendoGridColumnDefinition('regionName', this.appTextGridColumnHeader('region-name'), 70, 370),

            GridUtilities.kendoGridColumnDefinition('orderId', this.appTextGridColumnHeader('order-id'), 60, 380),
            GridUtilities.kendoGridColumnDefinition('bidDateValue', this.appTextGridColumnHeader('bid-date'), 70, 9, undefined, { format: '{0:d}' }),
            GridUtilities.kendoGridColumnDefinition('statusName', this.appTextGridColumnHeader('status'), 110, 420, ''),
            GridUtilities.kendoGridColumnDefinition('serviceLevelName', this.appTextGridColumnHeader('service-level'), 100, 400),

            GridUtilities.kendoGridColumnDefinition('associationAddress1', this.appTextGridColumnHeader('association-address1'), 1, 440, undefined, {}, undefined, 'grid-col-hidden-export-only'),
            GridUtilities.kendoGridColumnDefinition('associationAddress2', this.appTextGridColumnHeader('association-address2'), 1, 460, undefined, {}, undefined, 'grid-col-hidden-export-only'),
            GridUtilities.kendoGridColumnDefinition('associationCity', this.appTextGridColumnHeader('association-city'), 1, 480, undefined, {}, undefined, 'grid-col-hidden-export-only'),
            GridUtilities.kendoGridColumnDefinition('associationState', this.appTextGridColumnHeader('association-state'), 1, 500, undefined, {}, undefined, 'grid-col-hidden-export-only'),
            GridUtilities.kendoGridColumnDefinition('associationZip', this.appTextGridColumnHeader('association-zip'), 1, 520, undefined, {}, undefined, 'grid-col-hidden-export-only'),
        ],
        this.appText('no-results'),
        {
            saveChanges: (e) => { this.handleExportKML(e); },
            editable: true,
            toolbar: [
                'excel',
                {
                    name: 'save',
                    text: 'Export to Google Earth',
                    iconClass: 'k-icon k-i-globe'
                },
                'pdf',
            ]
        },
        this.appTextPageTitle()
    );

    public constructor(props: IAssociationExportOrdersProps) {
        super(props, 'app.association-export-orders.');
    }

    public componentDidMount() {
        const enforce = this.enforceAdminOrEmployeeOnly();

        if (enforce) { this.props.navigateTo(NavigationLinks.clientDashboardHome()); }
    }

    public onGridColumnEditable(e: any) {
        return false;
    }

    public extendGridData(records: Array<any>) {
        // Extend grid row data
        if (!records) { return []; }

        return records.map(r => {
            let record = _.cloneDeep(r);

            // Association name
            record.associationName = Formatters.getStr(record, 'association.name', '');

            // Association Address
            record.associationAddress = (record.association && record.association.location) ? Formatters.composeLocationFullAddress(record.association.location) : '';

            // Association Location Details for Map Export
            record.associationAddress1 = Formatters.getStr(record, 'association.location.address1');
            record.associationAddress1 = Formatters.getStr(record, 'association.location.address2');
            record.associationCity = Formatters.getStr(record, 'association.location.city');
            record.associationState = Formatters.getStr(record, 'association.location.stateCode');
            record.associationZip = Formatters.getStr(record, 'association.location.postalCode');

            // Association Location Details for Map Export
            record.associationLocation = (record.association && record.association.location) ? Formatters.composeLocationCityState(record.association.location) : '';
            record.associationLocationMapAddress1 = Formatters.getStr(record, 'association.location.address1');
            record.associationLocationMapCity = Formatters.getStr(record, 'association.location.city');
            record.associationLocationMapState = Formatters.getStr(record, 'association.location.stateCode');
            record.associationLocationMapZip = Formatters.getStr(record, 'association.location.postalCode');

            // Service level name
            record.serviceLevelName = Formatters.getStr(record, 'serviceLevel.name');

            // Status name
            record.statusName = Formatters.getStr(record, 'status.name');

            // Manager
            // record.managerUser = Formatters.getStr(record, 'user.username');

            // Coordinator
            // record.coordinatorUser = Formatters.getStr(record, 'siteInspector.username');

            // Order dates
            record.bidDateValue = record.bidDate ? new Date(record.bidDate) : '';

            // ASSOCIATION TYPE NAME
            record.associationTypeName = Formatters.getStr(record, 'association.associationType.name');

            // CLIENT REGION NAME
            record.regionName = Formatters.getStr(record, 'association.client.region.name');

            return record;
        });
    }

    public handleSelectStatusId(e: any) {
        const value = e ? e.value : 0;
        this.props.associationExportOrdersFilterStatusId(value);
    }

    public handleSelectRegionId(e: any) {
        const value = e ? e.value : 0;
        this.props.associationExportOrdersFilterRegionId(value);
    }

    public handleSelectFromBidDate(e: any) {
        const value = e ? e.format(this.MomentDateFormat) : '';
        this.props.associationExportOrdersFilterFromBidDate(value);
    }

    public handleSelectToBidDate(e: any) {
        const value = e ? e.format(this.MomentDateFormat) : '';
        this.props.associationExportOrdersFilterToBidDate(value);
    }

    public handleExportKML(e: any) {
        const token = _.get(this.props, 'authsession.access_token', '');

        const changeData = _.merge(
            {},
            {
                orders: _.map(_.get(this.props, 'search.results', []), (o) => {
                    const placemarkAddress = `${_.get(o, 'association.location.address1')} ${_.get(o, 'association.location.city')}, ${_.get(o, 'association.location.stateCode')} ${_.get(o, 'association.location.postalCode')}`;
                    const placemarkDescription = `Order #${_.get(o, 'orderId')}`;

                    return {
                        placemarkAddress: placemarkAddress,
                        placemarkDescription: placemarkDescription,
                        name: Formatters.getStr(o, 'association.name'),
                        address: Formatters.getStr(o, 'association.location.address1'),
                        city: Formatters.getStr(o, 'association.location.city'),
                        stateCode: Formatters.getStr(o, 'association.location.stateCode'),
                        postalCode: Formatters.getStr(o, 'association.location.postalCode'),
                        associationId: Formatters.getInt(o, 'associationId'),
                        orderId: Formatters.getInt(o, 'orderId'),
                    };
                })
            },
            {
                kmlConfig: {
                    folderName: 'Exported Orders',
                }
            }
        );

        this.props.downloadKML(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {

                const kmlFilename = `export-orders-${moment().format('l LT')}.kml`;
                const kmlData = Formatters.getStr(response, 'text');

                fileDownload(kmlData, kmlFilename);
            } else {
                // Error during creation...
            }
        });
    }

    public render() {
        const { search, filterByRegions, filterByStatuses, ...rest } = this.props;

        const rows = this.extendGridData(search.results);
        const dataSource = GridUtilities.kendoGridDataSourcePaged(rows, {
            sort: {
                dir: 'desc',
                field: 'bidDate',
            }
        });

        const filterOptionAny = this.appTextFilterOptionAny();
        const regions = FormUtilities.getExtendedFilterOptions(filterByRegions, 'regionId', 'label', filterOptionAny, -1);
        const statuses = FormUtilities.getExtendedFilterOptions(filterByStatuses, 'statusId', 'statusId', this.appText('filter-option-status-won-sent'), OrderStatuses.JOB_SENT_OR_WON_STATUSES, OrderStatuses.JOB_SENT_OR_WON_STATUSES);

        const fromBidDateSelected = FormUtilities.getDatePickerSelectedValue(this.props.search.filters.fromBidDate);
        const toBidDateSelected = FormUtilities.getDatePickerSelectedValue(this.props.search.filters.toBidDate);

        const pageTitle = this.appTextResultsAndPageTitle(rows.length, this.commonMessagePaths.searchResults, false, 'list-of-won-sent-jobs', true);

        const page = (
            <WhiteBoxPageSection title={pageTitle}>
                <Row>
                    <Col xs={12}>
                        <form onSubmit={e => { e.preventDefault(); }}>
                            <Row className="form-group">
                                <Col xs={3} md={4} xl={2}>
                                    <label className="control-label">{this.appText('filter-by-status-id')}</label>
                                    <VirtualizedSelect
                                        clearable={false}
                                        filterOptions={statuses.filter}
                                        name="status-filter"
                                        onChange={e => this.handleSelectStatusId(e)}
                                        options={statuses.options}
                                        value={search.filters.statusId}
                                    />
                                </Col>

                                <Col xs={3} md={4} xl={2}>
                                    <label className="control-label">{this.appText('filter-by-region-id')}</label>
                                    <VirtualizedSelect
                                        clearable={false}
                                        filterOptions={regions.filter}
                                        name="region-filter"
                                        onChange={e => this.handleSelectRegionId(e)}
                                        options={regions.options}
                                        value={search.filters.regionId}
                                    />
                                </Col>
                            </Row>

                            <Row className="form-group">
                                <Col xs={3} md={4} xl={2}>
                                    <label className="control-label">{this.appText('filter-by-from-bid-date')}</label>
                                    <DatePicker className="form-control" onChange={e => { this.handleSelectFromBidDate(e); }} selected={fromBidDateSelected} />
                                </Col>

                                <Col xs={3} md={4} xl={2}>
                                    <label className="control-label">{this.appText('filter-by-to-bid-date')}</label>
                                    <DatePicker className="form-control" onChange={e => { this.handleSelectToBidDate(e); }} selected={toBidDateSelected} />
                                </Col>
                            </Row>

                        </form>
                    </Col>
                </Row>

                <Row>
                    <Col xs={12}>
                        <div className="association-export-orders-table-wrapper">
                            <Grid dataSource={dataSource} {...this.gridOptions} />
                        </div>
                    </Col>
                </Row>
            </WhiteBoxPageSection>
        );

        return (
            <AppComp.RenderPageWithLayout branding={this.props.branding} pageName={pageTitle} {...rest}>
                {page}
            </AppComp.RenderPageWithLayout>
        );
    }
}

const getFiltersApiUrl = () => ApiEndpoints.getAssociationExportOrdersFiltersSearchUrl();
const getApiUrl = () => ApiEndpoints.getAssociationExportOrdersSearchUrl();

const mapPropsToConfigs = (props) => {
    const regionId = _.get(props, 'search.filters.regionId', -1);
    const statusId = _.get(props, 'search.filters.statusId', OrderStatuses.JOB_SENT_OR_WON_STATUSES);
    const fromBidDate = _.get(props, 'search.filters.fromBidDate', '');
    const toBidDate = _.get(props, 'search.filters.toBidDate', '');

    const currentFilters = {
        regionId,
        statusId,
        fromBidDate,
        toBidDate
    };

    const isInitialFilters = _.isEqual(currentFilters, _.pick(initialState, ['regionId', 'statusId', 'fromBidDate', 'toBidDate']));

    if (isInitialFilters) {
        const haveFilterOptions = (
            (props.filterByStatuses && props.filterByStatuses.length > 0) ||
            (props.filterByRegions && props.filterByRegions.length > 0)
        );

        if (haveFilterOptions) {
            const hasResults = Array.isArray(_.get(props, 'search.results')) && _.get(props, 'search.results.length', 0) > 0;

            if (props.dispatch && hasResults) {
                props.dispatch(updateEntities({ searchAssociationExportOrdersResults: () => [] }));
            }

            return null;
        }

        return (
            ReduxQueries.Queries.searchAssociationExportOrdersFiltersRequest(
                getFiltersApiUrl(),
                ReduxQueries.PropsAccessToken(props),
            )
        );
    }

    return (
        ReduxQueries.Queries.searchAssociationExportOrdersRequest(
            getApiUrl(),
            ReduxQueries.PropsAccessToken(props),
            regionId,
            statusId,
            fromBidDate,
            toBidDate,
        )
    );
};

const mapStateToProps = (state, props) => {
    return ReduxQueries.MapQueryStateToPropsByUrl(getApiUrl(), state, props, {
        filterByRegions: state.entities.searchAssociationExportOrdersRegions,
        filterByStatuses: state.entities.searchAssociationExportOrdersStatuses,
        search: {
            results: state.entities.searchAssociationExportOrdersResults,
            filters: {
                regionId: state.searchFiltersAssociationExportOrders.regionId,
                statusId: state.searchFiltersAssociationExportOrders.statusId,
                fromBidDate: state.searchFiltersAssociationExportOrders.fromBidDate,
                toBidDate: state.searchFiltersAssociationExportOrders.toBidDate,
            }
        },
    });
};

const mapDispatchToProps = dispatch => ({
    navigateTo: (path) => dispatch(push(path)),
    associationExportOrdersFilterStatusId: (v) => dispatch(Actions.associationExportOrdersFilterStatusId(v)),
    associationExportOrdersFilterRegionId: (v) => dispatch(Actions.associationExportOrdersFilterRegionId(v)),
    associationExportOrdersFilterFromBidDate: (v) => dispatch(Actions.associationExportOrdersFilterFromBidDate(v)),
    associationExportOrdersFilterToBidDate: (v) => dispatch(Actions.associationExportOrdersFilterToBidDate(v)),
    downloadKML: (d: any, a: string, o: boolean) => dispatch(MutateActions.downloadKMLMutateAsync(d, a, o)),
    dispatch: (d) => dispatch(d),
});

const AssociationExportOrdersContainer = compose<any>(
    connect(mapStateToProps, mapDispatchToProps),
    connectRequest(mapPropsToConfigs)
)(AssociationExportOrders);

export default withRouter(AssociationExportOrdersContainer);



// WEBPACK FOOTER //
// ./src/dashboard/association-export-orders.tsx